import { useEffect } from 'react';

import { countries } from 'constants/countries';
import { US_STATES } from 'constants/country-admin-divisions';
import { Currencies } from 'constants/currencies';
import patternValidators from 'helpers/validators/pattern-validators';
import { useForm } from 'react-hook-form';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button, Input, InputGroup, Select } from 'components/FormComponents';

export const NewCompanyModal = ({ visible, onClose = () => {}, onSubmit = () => {} }) => {
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      billing_currency: Currencies.aud,
      billing_entity: {},
    },
  });

  const companyCountry = watch('company.country');
  const isUSCompany = companyCountry === 'US';

  // Clear state when country changes away from US
  useEffect(() => {
    if (!isUSCompany) {
      setValue('company.state', null);
    }
  }, [isUSCompany, setValue]);

  return (
    <AnimatedModal visible={visible} onClose={onClose} title="Create company" small>
      <form onSubmit={handleSubmit(onSubmit)}>
        <h5 className="u-margin-top--large">Company</h5>
        <hr className="u-margin-top--0" />

        <InputGroup title="Name *">
          <Input
            register={register}
            name="company.name"
            placeholder="e.g. Checkmate"
            validators={{ required: true }}
            error={errors?.company?.name?.type}
          />
        </InputGroup>
        <InputGroup title="Account type *" className="u-width-100">
          <Select
            name="company.account_type"
            register={register}
            value={watch('company.account_type')}
            useDefault>
            <Select.Item key="simple" value="simple">
              Simple
            </Select.Item>
            <Select.Item key="enterprise" value="enterprise">
              Enterprise
            </Select.Item>
            <Select.Item key="internal" value="internal">
              Internal
            </Select.Item>
            <Select.Item key="demo" value="demo">
              Demo
            </Select.Item>
            <Select.Item key="sandbox" value="sandbox">
              Sandbox
            </Select.Item>
          </Select>
        </InputGroup>
        <InputGroup title="Account manager *" className="u-width-100">
          <Select
            name="company.account_manager"
            register={register}
            value={watch('company.account_manager')}
            className="u-capitalize"
            useDefault>
            {process.env.REACT_APP_ACCOUNT_MANAGERS.split(',').map(account_manager => (
              <Select.Item key={account_manager} className="u-capitalize" value={account_manager}>
                {account_manager}
              </Select.Item>
            ))}
            <Select.Item value="other">Other</Select.Item>
          </Select>
        </InputGroup>
        <InputGroup title="Support contact email">
          <Input
            register={register}
            name="company.contact_email"
            placeholder="support@checkmate.tech"
            validators={patternValidators.email}
            error={errors?.company?.contact_email?.message}
          />
        </InputGroup>
        <InputGroup title="Country">
          <Select name="company.country" register={register} value={companyCountry}>
            <Select.Item value="">Select Country</Select.Item>
            {countries.map(country => (
              <Select.Item key={country.alpha2} value={country.alpha2}>
                {country.name}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>
        {isUSCompany && (
          <InputGroup title="State">
            <Select name="company.state" register={register} value={watch('company.state')}>
              <Select.Item value="">Select State</Select.Item>
              {US_STATES.map(state => (
                <Select.Item key={state.value} value={state.value}>
                  {state.name}
                </Select.Item>
              ))}
            </Select>
          </InputGroup>
        )}

        <h5 className="u-margin-top--large">Billing</h5>
        <hr className="u-margin-top--0" />

        <InputGroup title="Currency *" className="u-width-100">
          <Select
            name="billing_currency"
            register={register}
            value={watch('billing_currency')}
            useDefault>
            <Select.Item value={Currencies.nzd}>NZD</Select.Item>
            <Select.Item value={Currencies.aud}>AUD</Select.Item>
            <Select.Item value={Currencies.usd}>USD</Select.Item>
          </Select>
        </InputGroup>
        <InputGroup title="Billing name">
          <Input
            register={register}
            name="billing_entity.invoice_entity_name"
            placeholder="Checkmate Technology Limited"
            validators={{ required: true }}
            error={errors?.billing_entity?.invoice_entity_name?.type}
          />
        </InputGroup>
        <InputGroup title="Email *">
          <Input
            register={register}
            name="billing_entity.email"
            placeholder="billing@checkmate.tech"
            validators={{ required: true, ...patternValidators.email }}
            error={errors?.billing_entity?.email?.message || errors?.billing_entity?.email?.type}
          />
        </InputGroup>
        <InputGroup title="Region">
          <Input register={register} name="billing_entity.region" placeholder="Tauranga" />
        </InputGroup>
        <InputGroup title="Postcode">
          <Input register={register} name="billing_entity.post_code" placeholder="3110" />
        </InputGroup>
        <InputGroup title="Country *">
          <Select
            name="billing_entity.country"
            register={register}
            value={watch('billing_entity.country')}
            useDefault>
            {countries.map(country => (
              <Select.Item key={country.value} value={country.value}>
                {country.name}
              </Select.Item>
            ))}
          </Select>
        </InputGroup>

        <h5 className="u-margin-top--large">Main user</h5>
        <hr className="u-margin-top--0" />

        <div className="u-width-100 d-flex justify-content-between align-items-start">
          <InputGroup title="First name *" className="u-width-40">
            <Input
              name="user.first_name"
              placeholder="John"
              register={register}
              validators={{ required: true }}
              error={errors?.user?.first_name?.type}
            />
          </InputGroup>
          <InputGroup title="Last name *" className="u-width-45">
            <Input
              name="user.last_name"
              placeholder="Doe"
              register={register}
              validators={{ required: true }}
              error={errors?.user?.last_name?.type}
            />
          </InputGroup>
        </div>
        <InputGroup title="Email *">
          <Input
            register={register}
            name="user.email"
            placeholder="john@checkmate.tech"
            validators={{ required: true, ...patternValidators.email }}
            error={errors?.user?.email?.message || errors?.user?.email?.type}
          />
        </InputGroup>

        <Button submit className="u-width-100 u-margin-top--large">
          Create
        </Button>
      </form>
    </AnimatedModal>
  );
};
